import { useEffect, useState } from "react";
import { coarsenNow } from "./price";

/** How often the price surfaces re-read the clock. A sale opens or closes
 *  within a minute of its stored boundary, which is as precise as a date-picked
 *  window ever needed to be. */
export const PRICE_CLOCK_STEP_MS = 60_000;

/**
 * The current wall clock, rounded down to `stepMs`.
 *
 * The clock is a genuine external system, so this is one of the effects that
 * SHOULD exist (react-useeffect-discipline): there is nothing to derive it from
 * during render, and nothing about it is an event. What the coarsening buys is
 * explained in `coarsenNow` — a Convex query argument is part of the
 * subscription key, and a raw `Date.now()` would re-subscribe every render.
 *
 * The initial value is computed in the `useState` initializer rather than in an
 * effect, so the first paint already has a usable time and the price does not
 * flash in.
 */
export function useCoarseNow(stepMs: number = PRICE_CLOCK_STEP_MS): number {
  const [now, setNow] = useState(() => coarsenNow(Date.now(), stepMs));
  useEffect(() => {
    const tick = () => {
      // `setState` with an unchanged value is a no-op re-render at worst, and
      // the coarse value only moves once per step.
      setNow(coarsenNow(Date.now(), stepMs));
    };
    tick();
    const id = setInterval(tick, stepMs);
    return () => {
      clearInterval(id);
    };
  }, [stepMs]);
  return now;
}
